/**
 * @fileoverview Écran d'entrée OTP du portail citoyen.
 *
 * Flux FR-04 en deux étapes : téléphone (+225, 10 chiffres) → code OTP à
 * 6 chiffres. Le renvoi de code est borné côté UI : cooldown de 30 s entre
 * deux envois et plafond de renvois simulé — reflet des plafonds anti-abus
 * du backend (5/min et 10/jour par numéro, 30/jour par IP), qui répondra
 * 429 au-delà. En maquette, aucun SMS n'est réellement envoyé.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  FormField,
  OtpInput,
  PhoneNumberInput,
  SuccessBanner,
  WarningNotice,
} from '@gnpc/ui';

type LoginStep = 'phone' | 'otp';

/** Délai (secondes) avant de pouvoir redemander un code. */
const RESEND_COOLDOWN_S = 30;

/**
 * Nombre de renvois avant l'état « quota atteint » — mock du 429 renvoyé
 * par le backend quand les plafonds anti-abus sont dépassés.
 */
const MAX_RESENDS = 3;

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Affordance de démo/captures : `?demo=otp` ouvre directement l'étape de
  // saisie du code (le second écran du flux FR-04), sans passer par la saisie
  // du téléphone. Sans effet sur le parcours réel.
  const [step, setStep] = useState<LoginStep>(params.get('demo') === 'otp' ? 'otp' : 'phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  // Le quota de renvoi vit au niveau de la PAGE, pas de l'étape : repasser
  // par « Modifier le numéro » puis « Recevoir le code » ne remet PAS ce
  // compteur à zéro. Sinon l'aller-retour téléphone → OTP contournerait le
  // plafond anti-abus simulé (le backend réel compte par numéro et par IP,
  // pas par écran, et répondrait 429 quoi qu'il arrive).
  const [resendCount, setResendCount] = useState(0);
  // Vrai dès qu'un premier code a été demandé : tout envoi suivant, y
  // compris via « Recevoir le code » après modification du numéro, compte
  // alors comme un renvoi dans le quota.
  const [hasRequestedCode, setHasRequestedCode] = useState(false);

  const phoneComplete = phone.length === 14;
  const otpComplete = otp.length === 6;
  const quotaReached = resendCount >= MAX_RESENDS;

  // Décompte du cooldown de renvoi — 1 tick/seconde tant qu'il est actif.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const interval = window.setInterval(() => {
      setResendCooldown((current) => Math.max(current - 1, 0));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [resendCooldown]);

  const requestCode = () => {
    setStep('otp');
    // Quota atteint : on affiche l'étape OTP (qui porte l'avertissement de
    // limite) sans simuler d'envoi supplémentaire ni relancer le cooldown.
    if (quotaReached) return;
    if (hasRequestedCode) setResendCount((current) => current + 1);
    setHasRequestedCode(true);
    setResendCooldown(RESEND_COOLDOWN_S);
  };

  const resendCode = () => {
    if (resendCooldown > 0 || quotaReached) return;
    setResendCount((current) => current + 1);
    setResendCooldown(RESEND_COOLDOWN_S);
  };

  return (
    <div className="bg-slate-50">
      <section className="portal-animated-hero bg-quipux-navy-950">
        <div className="relative z-10 mx-auto max-w-xl px-4 pb-36 pt-24">
          <header className="space-y-2">
            <p className="text-quipux-orange-400 text-sm font-medium">Bienvenue</p>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Portail unifié de paiement des services administratifs de transport et des amendes
            </h1>
            <p className="text-sm leading-6 text-white/70">
              Identifiez-vous avec le numéro de téléphone lié à votre compte Quipux pour rechercher
              et régler vos amendes et services de transport en ligne.
            </p>
          </header>
        </div>
      </section>

      <div className="relative z-10 mx-auto -mt-20 grid max-w-xl gap-6 px-4 pb-10">
        {step === 'phone' ? (
          <section className="border-border bg-card rounded-lg border p-5">
            <div className="grid gap-4">
              <FormField
                label="Téléphone du compte Quipux"
                required
                tooltip="Utilisez le numéro associé à votre compte Quipux."
                hint="Un code à usage unique sera envoyé par SMS à ce numéro."
              >
                <PhoneNumberInput value={phone} onChange={setPhone} />
              </FormField>
              <Button type="button" size="lg" disabled={!phoneComplete} onClick={requestCode}>
                Recevoir le code
              </Button>
            </div>
          </section>
        ) : (
          <section className="border-border bg-card rounded-lg border p-5">
            <div className="grid gap-4">
              {quotaReached ? (
                <WarningNotice title="Limite de codes atteinte">
                  Trop de codes ont été demandés pour ce numéro. Patientez quelques minutes
                  avant de réessayer — cette limite protège votre numéro contre les abus.
                </WarningNotice>
              ) : (
                <SuccessBanner title={resendCount > 0 ? 'Nouveau code envoyé' : 'Code envoyé'}>
                  Saisissez le code à 6 chiffres reçu par SMS pour continuer.
                  {resendCount > 0 && ' Le code précédent n’est plus valable.'}
                </SuccessBanner>
              )}
              <FormField
                label="Code OTP"
                required
                tooltip="Code à usage unique. En production, il expire après quelques minutes."
                hint="Vous pouvez coller les 6 chiffres d'un coup."
              >
                <OtpInput value={otp} onChange={setOtp} />
              </FormField>
              <Button
                type="button"
                size="lg"
                disabled={!otpComplete}
                onClick={() => navigate('/recherche')}
              >
                Continuer
              </Button>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={resendCooldown > 0 || quotaReached}
                  onClick={resendCode}
                >
                  {resendCooldown > 0
                    ? `Renvoyer le code (${resendCooldown} s)`
                    : 'Renvoyer le code'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setStep('phone')}>
                  Modifier le numéro
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
